<template>
  <div class="landing-page">
    <HeroSection />
    <BrandsSection />
    <HowItWorksSection />
    <FeaturesSection />
    <PlatformsSection />
    <TestimonialsSection />
    <PricingSection />
    <FaqSection />
    <BlogSection />
    <TeamSection />
    <CtaSection />
    <!-- <LandingFooter /> -->
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import HeroSection from '@/components/landing/HeroSection.vue';
import BrandsSection from '@/components/landing/BrandsSection.vue';
import HowItWorksSection from '@/components/landing/HowItWorksSection.vue';
import FeaturesSection from '@/components/landing/FeaturesSection.vue';
import PlatformsSection from '@/components/landing/PlatformsSection.vue';
import TestimonialsSection from '@/components/landing/TestimonialsSection.vue';
import PricingSection from '@/components/landing/PricingSection.vue';
import FaqSection from '@/components/landing/FaqSection.vue';
import BlogSection from '@/components/landing/BlogSection.vue';
import TeamSection from '@/components/landing/TeamSection.vue';
import CtaSection from '@/components/landing/CtaSection.vue';
import logoDark from '@/assets/images/logo-dark.png';
import { usePageSeo } from '@/composables/usePageSeo';
import {
  SITE_URL,
  buildOrganizationSchema,
  buildWebSiteSchema,
} from '@/config/seo';

const { t } = useI18n();

const logoAbsoluteUrl = computed(() => {
  if (typeof window === 'undefined') {
    return `${SITE_URL}${logoDark}`;
  }
  return new URL(logoDark, window.location.origin).href;
});

usePageSeo({
  titleKey: 'landing.page_title',
  descriptionKey: 'landing.page_description',
  keywords: computed(() => t('landing.meta_keywords')),
  jsonLd: computed(() => [
    buildOrganizationSchema(t, { logo: logoAbsoluteUrl.value }),
    buildWebSiteSchema(t),
  ]),
});

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.animate:not(.animate--infinite)').forEach(el => {
    observer.observe(el);
  });

});
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";

.landing-page {
  // Global landing page styles
  overflow-x: hidden;
  scroll-behavior: smooth;

  // Fade-in animation for sections when they come into view
  .animate {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }

    &.animate--delay-1 {
      transition-delay: 0.2s;
    }

    &.animate--delay-2 {
      transition-delay: 0.4s;
    }

    &.animate--delay-3 {
      transition-delay: 0.6s;
    }

    &.animate--infinite {
      opacity: 1;
      transform: translateY(0);
    }
  }

  // Override for landing page in dark mode
  .body--dark & {
    background-color: var(--color-background);
    color: var(--color-text);
  }

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>